import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, PopupData, Service } from 'webapp-types';
import { RcItem } from 'webapp/enums/enums';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import BarLoaderOverlayComponent from 'webapp/components/bar-loader-overlay';
import CommonAskToConnectButtonComponent from 'webapp/components/common/ask-to-connect-button';
import CommonSecurityInfoComponent from 'webapp/components/common/security-info';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ResolveDataItemChoiceComponent from 'webapp/components/resolve-data-item-choice';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';
import type SourceType from 'webapp/models-custom/source-type';

import type { onConnectSourceResult } from 'webapp/services/connect';

class SourceData {
	sourceType: SourceType;
	@tracked connectedSourceId?: string | null;
	messageId?: number;
	@tracked isSolving?: boolean;

	constructor(props: {
		sourceType: SourceType;
		connectedSourceId?: string;
		messageId?: number;
		isSolving?: boolean;
	}) {
		this.sourceType = props.sourceType;
		this.messageId = props.messageId;
		this.isSolving = props.isSolving;

		const connectedSources = props.sourceType.connectedSources,
			connectedSourceId = props.connectedSourceId;

		if (!connectedSourceId && connectedSources.length === 1) {
			this.connectedSourceId = connectedSources[0]!.id!;
		} else {
			this.connectedSourceId = connectedSourceId;
		}
	}
}

export default class BoardPopupResolveDataComponent extends PopupInnerModalComponent<PopupData.Board.Resolve> {
	@service('connect') declare connectService: Service.Connect;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	@tracked boardMessages: ResolutionCenterItem[] = [];
	@tracked override submitInProgress = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const boardMessages: ResolutionCenterItem[] = [],
			boardId = this.board.numericId;

		if (boardId) {
			this.resolutionCenterMessages.forEach((item) => {
				if (item.boardIds.includes(boardId)) {
					boardMessages.push(item);
				}
			});
		}

		this.boardMessages = boardMessages;
	}

	get board() {
		return this.popupData.model;
	}

	get resolutionCenterMessages() {
		return this.appNotificationsService.sortedVisibleNotifications;
	}

	get sourcesToConnect() {
		const allSources = this.mmSourcesService.savedRecords,
			sourceTypes = this.mmSourceTypesService.sourceTypeModels,
			sources: SourceData[] = [];

		this.boardMessages.forEach((message: ResolutionCenterItem) => {
			const dataSourceTypeId = message.params.dataSourceTypeId;
			sourceTypes.forEach((sourceType) => {
				if (
					message.type === RcItem.CONNECT_SOURCE &&
					sourceType.id === dataSourceTypeId &&
					!allSources.find((item) => item.type === dataSourceTypeId)
				) {
					sources.push(new SourceData({ messageId: message.id, sourceType }));
				}
			});
		});

		return sources;
	}

	@cached
	get sourcesToChoose() {
		const allSources = this.mmSourcesService.savedRecords,
			sourceTypes = this.mmSourceTypesService.sourceTypeModels,
			sources: SourceData[] = [];

		this.boardMessages.forEach((message: ResolutionCenterItem) => {
			const dataSourceTypeId = message.params.dataSourceTypeId;
			sourceTypes.forEach((sourceType) => {
				if (
					[RcItem.CONNECT_SOURCE, RcItem.CHOOSE_SOURCE].includes(message.type) &&
					sourceType.id === dataSourceTypeId &&
					allSources.find((item) => item.type === dataSourceTypeId)
				) {
					sources.push(
						new SourceData({
							messageId: message.id,
							sourceType,
							isSolving: false,
						}),
					);
				}
			});
		});

		return sources;
	}

	get isSecurityInfoShown() {
		return !!this.sourcesToConnect.length && !this.sourcesToChoose.length;
	}

	get isAllResolved() {
		return this.allSourcesToProcess.length === this.sourcesForResolving.length;
	}

	get isContinueButtonDisabled() {
		return !this.isAllResolved;
	}

	get allSourcesToProcess() {
		return [...this.sourcesToConnect, ...this.sourcesToChoose];
	}

	get sourcesForResolving(): SourceData[] {
		return this.allSourcesToProcess.filter((source) => !!source.connectedSourceId);
	}

	resolveSource(sourceDataForResolving: SourceData) {
		const messageId = sourceDataForResolving.messageId;
		if (!messageId) {
			return Promise.reject();
		}

		const typeId = sourceDataForResolving.sourceType.id,
			sourceId = sourceDataForResolving.connectedSourceId,
			onChooseEnd = () => {
				sourceDataForResolving.isSolving = false;
			},
			data: APIData.AppNotifications.ResolveMessageRequest = { dataSourceTypeMap: {} };

		data.dataSourceTypeMap[String(typeId)] = sourceId;

		sourceDataForResolving.isSolving = true;

		return this.requestService
			.apiWithAsyncReply('POST', `/resolution-center/${messageId}`, data)
			.then(
				() => {
					sourceDataForResolving.connectedSourceId = sourceId;
					onChooseEnd();
				},
				() => {
					onChooseEnd();
				},
			);
	}

	@action
	updateSourceId(sourceData: SourceData, sourceId: string) {
		sourceData.connectedSourceId = sourceId;
	}

	@action
	connect(sourceToResolve: SourceData) {
		void this.connectService
			.connectSourceFromIdOrKey(sourceToResolve.sourceType.key, {
				origin: 'modalResolvedata',
			})
			.then((result: onConnectSourceResult) => {
				if (result.isSuccess) {
					this.updateSourceId(sourceToResolve, String(result.source.id));
				}
			});
	}

	@action
	finishResolving() {
		this.submitInProgress = true;
		const promises: Promise<void>[] = [];
		this.sourcesForResolving.forEach((sourcesDataForResolving) => {
			promises.push(this.resolveSource(sourcesDataForResolving));
		});

		Promise.allSettled(promises)
			.then(() => {
				if (this.popupData.onResolveAll) {
					this.popupData.onResolveAll();
				}
				this.close();
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService))
			.finally(() => {
				this.submitInProgress = false;
			});
	}

	@action
	triggerLater(): void {
		this.popupData.onResolveAll?.();
		this.close();
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Connect" @size="lg" />
			Connect data sources used on '{{this.board.name}}'
		</div>
		<div class="content">
			<p>Dashboard '{{this.board.name}}' is using the following data sources:</p>
			<div class="actionList">
				<ul class="sources-map">
					{{#each this.sourcesToConnect as |source|}}
						<li class="t1">
							<AvatarOrInitialsComponent
								@name={{source.sourceType.name}}
								@avatar={{source.sourceType.avatar}}
							/>
							<span class="sourceTitle">{{source.sourceType.name}}</span>
							<div class="actions">
								<CommonAskToConnectButtonComponent @model={{source.sourceType}} />
								<SubmitButtonComponent
									@text="Connect"
									@icon="Connect"
									@noDelay={{true}}
									@onTrigger={{fn this.connect source}}
									@theme="green"
								/>
							</div>
						</li>
					{{/each}}
					{{#each this.sourcesToChoose as |source|}}
						<li class="t2">
							<AvatarOrInitialsComponent
								@name={{source.sourceType.name}}
								@avatar={{source.sourceType.avatar}}
							/>
							<span class="sourceTitle">{{source.sourceType.name}}</span>
							<ResolveDataItemChoiceComponent
								@sourceData={{source}}
								@onChoose={{this.updateSourceId}}
							/>
							<BarLoaderOverlayComponent @isActive={{source.isSolving}} />
						</li>
					{{/each}}
				</ul>
			</div>

			{{#if this.isSecurityInfoShown}}
				<CommonSecurityInfoComponent />
			{{/if}}

			<div class="controls2">
				{{#if this.isContinueButtonDisabled}}
					<div class="controls2__left">
						<SubmitButtonComponent
							@text="No thanks, I will connect later"
							@theme="grey-empty"
							@noDelay={{true}}
							@onTrigger={{this.triggerLater}}
						/>
					</div>
				{{/if}}
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Continue"
						@isDisabled={{this.isContinueButtonDisabled}}
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.finishResolving}}
						@extraClass="continue"
					/>
				</div>
			</div>
		</div>
	</template>
}
